// In-platform "Report a problem" widget — the floating help button big
// products keep in a corner. Backed by taskboards forms: a board admin
// publishes a form, enables "Platform widget" in its settings and picks
// the audience (everyone on the server / specific departments); this
// component fetches the forms visible to the current user and renders
// nothing when there are none (guests and non-members get an empty list
// from the server). Submitting goes through the SAME public submit
// pipeline as /form/:slug — the logged-in user's JWT rides along, so the
// created ticket carries reporterUserId ("Reported by" on the board).
import { useEffect, useState } from 'react'
import { ArrowLeft, LifeBuoy, Loader2, X } from 'lucide-react'
import { formsApi } from '@os/taskboards/web/services/forms'
import { usePublicForm } from '@os/taskboards/web/hooks/usePublicForm'
import FormRenderer from '@os/taskboards/web/pages/Public/FormRenderer'
import type { WidgetFormCard } from '@os/taskboards/web/types/forms'
import { IS_CAPACITOR } from '../../services/config'

export default function SupportWidget() {
  // Not on the mobile (Capacitor) shell: the floating button eats scarce
  // screen space and overlaps the chat composer / native controls there,
  // and screen-capture (getDisplayMedia) doesn't work in the WebView
  // anyway. Hidden entirely on mobile until it gets a native-shell design.
  if (IS_CAPACITOR) return null
  return <SupportWidgetInner />
}

function SupportWidgetInner() {
  const [forms, setForms] = useState<WidgetFormCard[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState<WidgetFormCard | null>(null)
  // Screenshot capture is in flight: hide the whole widget surface so the
  // grabbed frame shows the PAGE (the bug), not our modal on top of it.
  const [capturing, setCapturing] = useState(false)

  useEffect(() => {
    let alive = true
    formsApi.widgetVisible()
      .then((list) => { if (alive) setForms(list) })
      .catch(() => { if (alive) setForms([]) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    let failsafe: ReturnType<typeof setTimeout> | undefined
    const onStart = () => {
      setCapturing(true)
      // Failsafe: never leave the widget hidden if a capture:end is lost.
      clearTimeout(failsafe)
      failsafe = setTimeout(() => setCapturing(false), 125_000)
    }
    const onEnd = () => { clearTimeout(failsafe); setCapturing(false) }
    window.addEventListener('taskboards:capture:start', onStart)
    window.addEventListener('taskboards:capture:end', onEnd)
    return () => {
      clearTimeout(failsafe)
      window.removeEventListener('taskboards:capture:start', onStart)
      window.removeEventListener('taskboards:capture:end', onEnd)
    }
  }, [])

  if (forms.length === 0) return null

  function openWidget() {
    setActive(forms.length === 1 ? forms[0] : null)
    setOpen(true)
  }
  function close() {
    setOpen(false)
    setActive(null)
  }

  return (
    <>
      {/* A slim tab tucked against the RIGHT edge, lifted to ~22% from the
          bottom so it clears the chat composer / send button. It sits
          mostly off-screen (translate-x) and slides out on hover/focus —
          eating almost no space until the user reaches for it. */}
      <button
        onClick={openWidget}
        title="Report a problem"
        aria-label="Report a problem"
        className="group/fab fixed right-0 bottom-[22%] z-[55] flex items-center gap-2
                   rounded-l-lg bg-primary text-primary-foreground shadow-lg
                   pl-2.5 pr-3 py-2.5
                   translate-x-[calc(100%-2rem)] hover:translate-x-0 focus-visible:translate-x-0
                   transition-transform duration-200 ease-out"
      >
        <LifeBuoy size={18} className="shrink-0" />
        <span className="text-xs font-medium whitespace-nowrap opacity-0 group-hover/fab:opacity-100 group-focus-visible/fab:opacity-100 transition-opacity">
          Report a problem
        </span>
      </button>

      {open && (
        <div
          className={`fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4 ${capturing ? 'opacity-0 pointer-events-none' : ''}`}
          onClick={close}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Report a problem"
            className="bg-background rounded-xl ring-1 ring-border shadow-2xl w-[620px] max-w-full max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center gap-2 px-5 py-3 border-b border-border bg-card shrink-0">
              {active && forms.length > 1 && (
                <button onClick={() => setActive(null)} className="text-muted-foreground hover:text-foreground transition" aria-label="Back to form list">
                  <ArrowLeft size={16} />
                </button>
              )}
              <h3 className="text-sm font-semibold text-foreground truncate">{active ? active.name : 'Report a problem'}</h3>
              <button onClick={close} className="ml-auto text-muted-foreground hover:text-foreground transition" aria-label="Close">
                <X size={16} />
              </button>
            </header>
            <div className="flex-1 min-h-0 overflow-y-auto p-5">
              {active ? (
                <WidgetFormBody key={active.id} card={active} />
              ) : (
                <ul className="space-y-2">
                  {forms.map((f) => (
                    <li key={f.id}>
                      <button
                        onClick={() => setActive(f)}
                        className="w-full text-left bg-card ring-1 ring-border rounded-lg px-4 py-3 hover:bg-muted/40 transition"
                      >
                        <div className="text-sm font-medium text-foreground">{f.name}</div>
                        {f.description && <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{f.description}</div>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// The card only carries id/slug/name — the form's config comes from the
// same public endpoint the standalone /form/:slug page uses (the form is
// guaranteed published while widget-enabled).
function WidgetFormBody({ card }: { card: WidgetFormCard }) {
  const { form, loading, error } = usePublicForm(card.slug)
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 size={15} className="animate-spin" /> Loading…
      </div>
    )
  }
  if (error || !form) {
    return <div className="py-10 text-center text-sm text-muted-foreground">This form is unavailable right now.</div>
  }
  return (
    <>
      {form.description && <p className="text-sm text-muted-foreground mb-4">{form.description}</p>}
      <FormRenderer slug={form.slug} config={form.config} channel="widget" />
    </>
  )
}
